import { ReactElement, useEffect, useState } from 'react';
import { GroupRenderer } from './group-render';
import questionnaireService from '../../services/QuestionnaireService';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';
import { FormValue, Question } from '@app/types';

interface QuestionnaireGroupProps {
  questions: Question[];
  values: Record<string, FormValue>;
  setFieldValue: (key: string, value: FormValue) => void;
  errors: Record<string, boolean>;
  validateField: (key: string) => void;
}

export const QuestionnaireGroup = ({
  questions,
  values,
  setFieldValue,
  errors,
  validateField
}: QuestionnaireGroupProps): ReactElement => {
  const { t } = useTranslation();
  const [timeout, setT] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (questions) {
      questions.forEach((question) => {
        if (question.default && !values[question.groupKey || question.key]) {
          setFieldValue(question.groupKey || question.key, question.default);
        }
      });
    }
  }, []);

  const setValues = (updateKeys: Set<string> | string[]) => {
    setTimeout(() => {
      updateKeys?.forEach((key) => {
        setFieldValue(key, values[key]);
      });
    }, 50);
  };

  const removeAction = (targets: string[], cb: (updateKeys: Set<string>) => void) => {
    const updateKeys = new Set<string>();
    for (const target of targets) {
      let targetKey = target.replace(/\$/, '');
      const haveArrayValue = targetKey.match(/(\..*)/);

      if (haveArrayValue?.length) {
        const arrayValue = haveArrayValue[1].replace('.', '');
        targetKey = targetKey.replace(/(\..*)/, '');
        values[targetKey] = [...((values[targetKey] || []) as string[])].filter((value) => {
          return `${value}` !== `${arrayValue}`;
        });
        updateKeys.add(targetKey);
      } else {
        values[targetKey] = null;
        updateKeys.add(targetKey);
      }
    }

    if (updateKeys) {
      cb(updateKeys);
    }
  };

  const setValue = (target: string, cb: (keys: string[]) => void) => {
    const matches = target.match(/(\$[a-zA-Z0-9_]+).+?=[\s+]([a-z0-9A-Z_:.['\]]+)/g);
    if (!matches?.length) return;

    const updatedKeys: string[] = [];
    matches.forEach((match) => {
      const keyMatch = match.match(/\$([a-zA-Z0-9_]+)/);
      const valueMatch = match.match(/=[\s+]([a-z0-9A-Z_:.['\]]+)/);
      if (!keyMatch?.[1] || !valueMatch?.[1]) return;

      const key = keyMatch[1];
      let value = valueMatch[1];

      if (value[0] === '[') {
        try {
          values[key] = JSON.parse(value);
        } catch {
          console.error('Invalid array value:', value);
          return;
        }
      } else {
        if (value.startsWith("'") && value.endsWith("'")) {
          value = value.slice(1, -1);
        }
        values[key] = value;
      }
      updatedKeys.push(key);
    });

    if (updatedKeys.length) {
      cb(updatedKeys);
    }
  };

  const setValueByCalculation = (target: string, cb: (keys: string[]) => void) => {
    const variable = target.match(/^#([a-zA-Z0-9_]+).*?=/);
    if (variable?.length === 2) {
      target = target.replace(variable[0], '');
    } else {
      return;
    }

    const targetValues = target.match(/(\$[a-zA-Z0-9_]+)/g) ?? [];
    if (targetValues?.length > 0) {
      for (const targetValue of targetValues) {
        const [replaceKey, value] = questionnaireService.replacePlaceholderWithValue(
          targetValue,
          values
        );
        target = target.replace(replaceKey, value);
      }

      try {
        values[variable[1]] = (function () {
          this.calculateFrom = calculateFrom;

          // eslint-disable-next-line no-eval
          return eval(target);
        })();
        cb([variable[1]]);
      } catch (e) {
        console.log('a', e);
      }
    }
  };

  const calculateFrom = (joint: string, key: string, value: string) => {
    if (key === 'NO_PREVSURG') {
      if (value === 'null' || !value) {
        return '1';
      }

      const [jointKey, jointType] = joint.split('_');
      let numberOfOptions = value.length;
      const question = questionnaireService.findQuestion(
        t(`spine:joints.${jointKey}.${jointType}`, {
          returnObjects: true
        }) as Question[],
        key
      );
      if (!question) {
        return;
      }

      switch (numberOfOptions) {
        case 1:
        case 2:
          return value.indexOf('1') > -1 ? '1' : '2';
        case 3:
        case 4:
          return '3';
        default:
          return numberOfOptions >= 5 ? '4' : undefined;
      }
    }
  };

  const setDefaults = () => {
    const questionsWithDefaults = questions.filter((question) => question.default);
    if (questionsWithDefaults.length > 0) {
      for (const question of questionsWithDefaults) {
        if (!values[question.key]) {
          values[question.key] = question.default as string;
        }
      }
    }
  };

  const onSetFieldValue = (key: string, value: FormValue) => {
    setDefaults();

    const question = questions.find((q) => q.key === key);
    if (question?.actions?.length) {
      question.actions.forEach((action) => {
        if (
          questionnaireService.evaluateQuestion(action.rule, {
            ...values,
            [key]: value
          })
        ) {
          switch (action.action) {
            case 'remove':
              action.targets && removeAction(action.targets, setValues);
              break;
            case 'set':
              action.target && setValue(action.target, setValues);
              break;
            case 'calculate':
              action.target && setValueByCalculation(action.target, setValues);
              break;
          }
        }
      });
    }

    setFieldValue(key, value);
    validate(key);
  };

  const validate = (key: string) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    setT(
      setTimeout(() => {
        try {
          validateField(key);
        } catch (e) {
          /* */
        }
      }, 100)
    );
  };

  const getQuestions = () => {
    const groupedBy = groupBy(
      questionnaireService
        .prepareQuestions(1, questions, values)
        ?.filter((question) => question?.type !== 'hidden'),
      (g) => g.groupedBy
    );

    return Object.keys(groupedBy).map((gKey) => {
      return {
        group: groupedBy[gKey][0].group,
        groupedBy: groupedBy[gKey][0].groupedBy,
        key: groupedBy[gKey][0].key,
        type: groupedBy[gKey][0].type,
        options: groupedBy[gKey]
      };
    });
  };

  return (
    <div className={'questionnaire-form-group'}>
      {getQuestions()?.map((group) => {
        return (
          <GroupRenderer
            key={group.key}
            group={group}
            values={values}
            setFieldValue={onSetFieldValue}
            errors={errors}
          />
        );
      })}
    </div>
  );
};
